import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  PartnerGoalTarget,
  PartnerGoalTargetUpdateInput,
} from "@colab/shared";

export type UpdateGoalTargetInput = {
  id: string;
  /**
   * The goal this target belongs to (caller already knows this from the
   * row it's mutating). Used for cache invalidation only — the URL carries
   * only `id`, mirroring the legacy `(id, goalId, partnerId, patch)`
   * signature where goalId + partnerId are also cache-only.
   */
  goalId: string;
  partnerId: string;
  patch: PartnerGoalTargetUpdateInput;
};

type UpdateTargetResponse = { ok: true; target: PartnerGoalTarget };

// PATCH /api/admin/goals/targets/:id
export function useUpdateGoalTarget() {
  const queryClient = useQueryClient();
  return useMutation<UpdateTargetResponse, ApiClientError, UpdateGoalTargetInput>({
    mutationFn: ({ id, patch }) =>
      apiFetch<UpdateTargetResponse>(`/admin/goals/targets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, { goalId, partnerId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.targetsForGoal(goalId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.detail(goalId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.forPartner(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.partners.detail(partnerId),
      });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update target");
    },
  });
}
